import React, { useEffect, useState } from 'react';
import './cvfolio/cvfolio.css';

const CvfolioTemplate = ({ subdomain }) => {
  const [hero, setHero] = useState(null);
  const [experiences, setExperiences] = useState([]);
  const [education, setEducation] = useState([]);
  const [competencies, setCompetencies] = useState([]);
  const [tools, setTools] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('home');
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://personal-site-saas-api.l5819033.workers.dev';
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const normalizeUrl = (value) => {
    if (!value || typeof value !== 'string') return '';
    const v = value.trim();
    if (v.startsWith('http://') || v.startsWith('https://')) return v;
    return `https://${v}`;
  };

  const safeHostname = (value) => {
    try {
      const url = new URL(normalizeUrl(value));
      return url.hostname;
    } catch {
      return '';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr.length < 4) return '';
    // YYYY-MM-DD formatı varsa ayı da göster
    if (dateStr.length >= 7 && dateStr.includes('-')) {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        return `${months[date.getMonth()]} ${date.getFullYear()}`;
      }
    }
    // Sadece yıl varsa
    const year = dateStr.slice(0, 4);
    return year;
  };

  const getCurrentJob = () => {
    if (!experiences || experiences.length === 0) return null;
    const sorted = [...experiences].sort((a, b) => {
      const aEnd = a.is_current ? 9999 : (a.end_date ? parseInt(String(a.end_date).slice(0,4)) : 0);
      const bEnd = b.is_current ? 9999 : (b.end_date ? parseInt(String(b.end_date).slice(0,4)) : 0);
      return bEnd - aEnd;
    });
    return sorted[0];
  };

  const getLanguageFlagCode = (language) => {
    // language.code varsa kullan (örn: "en", "tr", "fr")
    if (language.code) {
      const langCode = language.code.toLowerCase();
      // Bazı dil kodlarını ülke kodlarına çevir
      const langToCountry = {
        'en': 'gb', 'tr': 'tr', 'fr': 'fr', 'de': 'de', 'es': 'es',
        'it': 'it', 'pt': 'pt', 'nl': 'nl', 'pl': 'pl', 'ru': 'ru',
        'ja': 'jp', 'ko': 'kr', 'zh': 'cn', 'ar': 'sa', 'hi': 'in'
      };
      return langToCountry[langCode] || langCode;
    }
    // country_code varsa kullan
    if (language.country_code) {
      return language.country_code.toLowerCase();
    }
    // language.name veya language.language varsa mapping kullan
    const nameToCode = {
      'Turkish': 'tr', 'Türkçe': 'tr',
      'English': 'gb', 'İngilizce': 'gb',
      'German': 'de', 'Almanca': 'de',
      'French': 'fr', 'Fransızca': 'fr',
      'Spanish': 'es', 'İspanyolca': 'es',
      'Italian': 'it', 'İtalyanca': 'it',
      'Portuguese': 'pt', 'Portekizce': 'pt',
      'Dutch': 'nl', 'Felemenkçe': 'nl',
      'Polish': 'pl', 'Lehçe': 'pl',
      'Russian': 'ru', 'Rusça': 'ru',
      'Japanese': 'jp', 'Japonca': 'jp',
      'Korean': 'kr', 'Korece': 'kr',
      'Chinese': 'cn', 'Çince': 'cn',
      'Arabic': 'sa', 'Arapça': 'sa',
      'Hindi': 'in', 'Hintçe': 'in'
    };
    const langName = (language.name || language.language || '').trim();
    if (langName && nameToCode[langName]) {
      return nameToCode[langName];
    }
    return null;
  };

  const handleContactChange = (e) => {
    setContactFormData({
      ...contactFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/site/${subdomain}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactFormData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSubmitStatus('success');
      setContactFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        setLoading(true);
        const API = process.env.REACT_APP_API_URL || 'https://personal-site-saas-api.l5819033.workers.dev';
        const toJson = async (res) => {
          if (!res.ok) {
            const text = await res.text();
            throw new Error(`HTTP ${res.status} ${res.statusText}: ${text.substring(0,200)}`);
          }
          return res.json();
        };

        const [heroRes, expRes, eduRes, compRes, toolsRes, langRes] = await Promise.all([
          fetch(`${API}/api/site/${subdomain}/hero`),
          fetch(`${API}/api/site/${subdomain}/experiences`),
          fetch(`${API}/api/site/${subdomain}/education`),
          fetch(`${API}/api/site/${subdomain}/competencies`),
          fetch(`${API}/api/site/${subdomain}/tools`),
          fetch(`${API}/api/site/${subdomain}/languages`)
        ]);
        if (!active) return;
        setHero(heroRes.ok ? await toJson(heroRes) : null);
        setExperiences(expRes.ok ? await toJson(expRes) : []);
        setEducation(eduRes.ok ? await toJson(eduRes) : []);
        setCompetencies(compRes.ok ? await toJson(compRes) : []);
        setTools(toolsRes.ok ? await toJson(toolsRes) : []);
        setLanguages(langRes.ok ? await toJson(langRes) : []);
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, [subdomain]);

  if (loading) {
    return (
      <div className="cvf-page">
        <div className="cvf-container" style={{paddingTop:32}}>
          <div className="cvf-skel cvf-skel-avatar" />
          <div className="cvf-skel cvf-skel-line" />
          <div className="cvf-skel cvf-skel-line" style={{width:'70%'}} />
        </div>
      </div>
    );
  }

  return (
    <div className="cvf-page">
      <header className="cvf-header">
        <div className="cvf-header-inner">
          <nav className="cvf-nav">
            <button type="button" className={`cvf-chip ${tab==='home'?'cvf-chip-active':''}`} onClick={() => setTab('home')}>Home</button>
            <button type="button" className={`cvf-chip ${tab==='work'?'cvf-chip-active':''}`} onClick={() => setTab('work')}>Work</button>
            <button type="button" className={`cvf-chip ${tab==='education'?'cvf-chip-active':''}`} onClick={() => setTab('education')}>Education</button>
            <button type="button" className={`cvf-chip ${tab==='skills'?'cvf-chip-active':''}`} onClick={() => setTab('skills')}>Skills</button>
            <button type="button" className={`cvf-chip ${tab==='contact'?'cvf-chip-active':''}`} onClick={() => setTab('contact')}>Contact</button>
          </nav>
        </div>
      </header>

      <main className="cvf-container">
        <section className="cvf-hero">
          <div className="cvf-avatar" aria-hidden="true">{(hero?.name || 'U').slice(0,1)}</div>
          <div className="cvf-hero-text">
            <h1 className="cvf-title">{hero?.name || 'Your Name'}</h1>
            <p className="cvf-subtitle">
              {(() => {
                const job = getCurrentJob();
                if (job && job.title && job.company) {
                  return `${job.title} @ ${job.company}`;
                }
                return hero?.title || hero?.current_job || 'Title / Location';
              })()}
            </p>
            {hero?.github_url && (
              <span className="cvf-tag">{(hero.github_url.split('/').pop() || '').toLowerCase()}</span>
            )}
          </div>
        </section>

        {tab === 'home' && (
        <section id="cvf-about" className="cvf-section cvf-fade">
          <h2 className="cvf-section-title">About</h2>
          <p className="cvf-p">
            {hero?.description || 'Write a short about text from your dashboard.'}
          </p>
          <h3 className="cvf-section-title" style={{marginTop: '18px', marginBottom: '12px'}}>Contacts</h3>
          <div className="cvf-contacts">
            {hero?.linkedin_url && (
              <div className="cvf-row">
                <span className="cvf-label">LinkedIn</span>
                <a href={normalizeUrl(hero.linkedin_url)} target="_blank" rel="noreferrer" className="cvf-social-link">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#2b2b2b"/></svg>
                  {safeHostname(hero.linkedin_url) || 'LinkedIn'}
                </a>
              </div>
            )}
            {hero?.github_url && (
              <div className="cvf-row">
                <span className="cvf-label">GitHub</span>
                <a href={normalizeUrl(hero.github_url)} target="_blank" rel="noreferrer" className="cvf-social-link">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" fill="#2b2b2b"/></svg>
                  {safeHostname(hero.github_url) || 'GitHub'}
                </a>
              </div>
            )}
            {hero?.cv_url && (
              <div className="cvf-row">
                <span className="cvf-label">Resume</span>
                <a href={normalizeUrl(hero.cv_url)} target="_blank" rel="noreferrer" className="cvf-social-link">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11zm-5-7v-2h2v2h-2zm0 4v-2h4v2h-4z" fill="#2b2b2b"/></svg>
                  Download
                </a>
              </div>
            )}
          </div>
        </section>
        )}

        {tab === 'work' && (
        <section id="cvf-work" className="cvf-section cvf-fade">
          <h2 className="cvf-section-title">Work Experience</h2>
          <div className="cvf-exp-list">
            {[...experiences].sort((a, b) => {
              const aEnd = a.is_current ? 9999 : (a.end_date ? parseInt(String(a.end_date).slice(0,4)) : 0);
              const bEnd = b.is_current ? 9999 : (b.end_date ? parseInt(String(b.end_date).slice(0,4)) : 0);
              return bEnd - aEnd || (parseInt(String(b.start_date || '').slice(0,4)) - parseInt(String(a.start_date || '').slice(0,4)));
            }).map((exp, i) => (
              <article key={i} className="cvf-exp">
                <div className="cvf-exp-side">
                  <div className="cvf-exp-period">
                    {formatDate(exp.start_date) || ''}
                    {exp.is_current ? ' – Now' : (exp.end_date ? ` – ${formatDate(exp.end_date)}` : '')}
                  </div>
                </div>
                <div className="cvf-exp-body">
                  <h3 className="cvf-exp-title">{exp.title || exp.position || ''}</h3>
                  <div className="cvf-exp-company">{exp.company || ''}</div>
                  {exp.description && <p className="cvf-p">{exp.description}</p>}
                  {Array.isArray(exp.achievements) && exp.achievements.length > 0 && exp.achievements.some(a => a.achievement && a.achievement.trim() !== '') && (
                    <ul className="cvf-ul">
                      {exp.achievements.filter(a => a.achievement && a.achievement.trim() !== '').map((a, idx) => (
                        <li key={idx}>{a.achievement}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
        )}

        {tab === 'education' && (
        <section id="cvf-education" className="cvf-section cvf-fade">
          <h2 className="cvf-section-title">Education</h2>
          <div className="cvf-exp-list">
            {[...education].sort((a, b) => {
              const aEnd = a.is_current ? 9999 : (a.end_date ? parseInt(String(a.end_date).slice(0,4)) : 0);
              const bEnd = b.is_current ? 9999 : (b.end_date ? parseInt(String(b.end_date).slice(0,4)) : 0);
              return bEnd - aEnd || (parseInt(String(b.start_date || '').slice(0,4)) - parseInt(String(a.start_date || '').slice(0,4)));
            }).map((e, i) => (
              <article key={i} className="cvf-exp">
                <div className="cvf-exp-side">
                  <div className="cvf-exp-period">
                    {formatDate(e.start_date) || ''}
                    {e.is_current ? ' – Now' : (e.end_date ? ` – ${formatDate(e.end_date)}` : '')}
                  </div>
                </div>
                <div className="cvf-exp-body">
                  <h3 className="cvf-exp-title">{e.field_of_study || e.field || ''}</h3>
                  <div className="cvf-exp-company">{e.institution || e.school || ''}</div>
                  {e.degree && <p className="cvf-p">{e.degree}</p>}
                  {e.description && <p className="cvf-p">{e.description}</p>}
                  {Array.isArray(e.achievements) && e.achievements.length > 0 && e.achievements.some(a => a.achievement && a.achievement.trim() !== '') && (
                    <div className="cvf-achievements">
                      {e.achievements.filter(a => a.achievement && a.achievement.trim() !== '').map((a, idx) => (
                        <p key={idx} className="cvf-p">{a.achievement}</p>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
        )}

        {tab === 'skills' && (
        <section id="cvf-skills" className="cvf-section cvf-fade">
          {competencies.length > 0 && (
            <div className="cvf-skills-group">
              <h3 className="cvf-skills-subtitle">Competencies</h3>
              <div className="cvf-chips">
                {competencies.map((c, i) => (
                  <span key={i} className="cvf-skill-chip">{c.name}</span>
                ))}
              </div>
            </div>
          )}
          {tools.length > 0 && (
            <div className="cvf-skills-group">
              <h3 className="cvf-skills-subtitle">Tools</h3>
              <div className="cvf-chips">
                {tools.map((t, i) => (
                  <span key={i} className="cvf-skill-chip">{t.name}</span>
                ))}
              </div>
            </div>
          )}
          {languages.length > 0 && (
            <div className="cvf-skills-group">
              <h3 className="cvf-skills-subtitle">Languages</h3>
              <div className="cvf-chips">
                {languages.map((lang, i) => {
                  const flagCode = getLanguageFlagCode(lang);
                  return (
                    <span key={i} className="cvf-skill-chip cvf-language-chip">
                      {flagCode && (
                        <span 
                          className={`fi fi-${flagCode}`} 
                          style={{
                            marginRight: '6px',
                            width: '20px',
                            height: '15px',
                            display: 'inline-block',
                            verticalAlign: 'middle'
                          }}
                        />
                      )}
                      {lang.name || lang.language || 'Language'}
                      {lang.level && <span style={{marginLeft: '6px', opacity: 0.7}}>({lang.level})</span>}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </section>
        )}

        {tab === 'contact' && (
        <section id="cvf-contact" className="cvf-section cvf-fade">
          <h2 className="cvf-section-title">Contact</h2>
          <p className="cvf-p" style={{ marginBottom: '24px' }}>
            Bana ulaşmak isterseniz aşağıdaki formu doldurabilirsiniz.
          </p>
          
          <form onSubmit={handleContactSubmit} style={{ maxWidth: '600px' }}>
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  color: '#2b2b2b'
                }}>
                  İsim
                </label>
                <input 
                  className="cvf-input" 
                  placeholder="Adınız Soyadınız" 
                  name="name"
                  value={contactFormData.name}
                  onChange={handleContactChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    backgroundColor: '#fff'
                  }}
                />
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  color: '#2b2b2b'
                }}>
                  Email
                </label>
                <input 
                  className="cvf-input" 
                  type="email"
                  placeholder="email@example.com" 
                  name="email"
                  value={contactFormData.email}
                  onChange={handleContactChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    backgroundColor: '#fff'
                  }}
                />
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  color: '#2b2b2b'
                }}>
                  Konu
                </label>
                <input 
                  className="cvf-input" 
                  placeholder="Mesaj konusu" 
                  name="subject"
                  value={contactFormData.subject}
                  onChange={handleContactChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    backgroundColor: '#fff'
                  }}
                />
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  color: '#2b2b2b'
                }}>
                  Mesaj
                </label>
                <textarea 
                  className="cvf-textarea" 
                  rows={6} 
                  placeholder="Mesajınızı buraya yazın..." 
                  name="message"
                  value={contactFormData.message}
                  onChange={handleContactChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    backgroundColor: '#fff',
                    minHeight: '120px'
                  }}
                />
              </div>
              
              {submitStatus === 'success' && (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#d4edda', 
                  color: '#155724', 
                  borderRadius: '8px',
                  border: '1px solid #c3e6cb',
                  fontSize: '14px'
                }}>
                  Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapılacaktır.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#f8d7da', 
                  color: '#721c24', 
                  borderRadius: '8px',
                  border: '1px solid #f5c6cb',
                  fontSize: '14px'
                }}>
                  Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyin.
                </div>
              )}
              
              <button 
                type="submit"
                disabled={isSubmitting}
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#2b2b2b',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.6 : 1,
                  alignSelf: 'flex-start',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.backgroundColor = '#3b3b3b';
                    e.target.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.target.style.backgroundColor = '#2b2b2b';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                {isSubmitting ? 'Gönderiliyor...' : 'Mesaj Gönder'}
              </button>
            </div>
          </form>
        </section>
        )}

      </main>
    </div>
  );
};

export default CvfolioTemplate;


